// 写作教练（lingzhi-studio 的 /studio/api/write）。和灵智原生同源、同一个登录令牌。
// 工作复盘、周复盘存进知识库「英文日报」；私人日记只在 lingzhi-studio。
import { request } from './client'
import { createSseParser } from '../lib/sse'

const BASE = '/studio/api/write'

export type EntryKind = 'work' | 'journal'
export const KIND_LABEL: Record<EntryKind, string> = { work: '工作复盘', journal: '私人日记' }

export interface ContextRef {
  knowledgeId: string
  title: string
}
export interface ChatMessage {
  role: 'user' | 'assistant'
  text: string
  at: string
}
export interface Draft {
  id: string
  entryId?: string
  kind: EntryKind
  title: string
  body: string
  revision: number
  contextRefs: ContextRef[]
  conversation: ChatMessage[]
  discardedAt?: string
  createdAt: string
  updatedAt: string
}
/** 存进知识库的那一篇（灵智原生的笔记，重新归档时原地更新）。 */
export interface SavedDoc {
  knowledgeId: string
  kbId: string
  title: string
}
export interface Entry {
  id: string
  kind: EntryKind
  title: string
  body: string
  createdAt: string
  updatedAt: string
  saved?: SavedDoc
  /** 归档以后从这一篇里记下的写法问题（后台整理，刚归档时还没有）；occurrences 是累计第几次。 */
  learned?: { category: string; explanationZh: string; example: string; occurrences: number }[]
  learnError?: string
}
export interface DraftSummary {
  id: string
  entryId?: string
  kind: EntryKind
  title: string
  words: number
  createdAt: string
  updatedAt: string
}
export interface EntrySummary {
  id: string
  kind: EntryKind
  title: string
  words: number
  createdAt: string
  updatedAt: string
  knowledgeId?: string
  kbId?: string
}
export interface Pattern {
  category: string
  explanationZh: string
  occurrences: number
  firstSeenAt: string
  lastSeenAt: string
  examples: string[]
}
export interface Profile {
  updatedAt: string | null
  patterns: Pattern[]
}
export interface EditProposal {
  baseRevision: number
  from: number
  to: number
  original: string
  replacement: string
  summaryZh: string
  reasons: string[]
}
export interface Review {
  strengths: string[]
  priorities: string[]
  issues: { category: string; explanationZh: string; example: string }[]
  exercise: string
}
export interface Weekly {
  week: string
  title: string
  body: string
  entryCount: number
  generatedAt: string
  saved?: SavedDoc & { savedAt: string }
}

const data = <T>(p: Promise<{ data: T }>) => p.then((r) => r.data)

export const timeline = () => data(request<{ data: { drafts: DraftSummary[]; entries: EntrySummary[] } }>(BASE))
export const createDraft = (kind: EntryKind) => data(request<{ data: Draft }>(`${BASE}/drafts`, { body: { kind } }))
export const getDraft = (id: string) => data(request<{ data: Draft }>(`${BASE}/drafts/${id}`))
export const saveDraft = (id: string, d: { revision: number; title: string; body: string; contextRefs: ContextRef[] }) =>
  data(request<{ data: Draft }>(`${BASE}/drafts/${id}`, { method: 'PUT', body: d }))
export const discardDraft = (id: string) => data(request<{ data: Draft }>(`${BASE}/drafts/${id}/discard`, { body: {} }))
export const restoreDraft = (id: string) => data(request<{ data: Draft }>(`${BASE}/drafts/${id}/restore`, { body: {} }))
export const editSelection = (id: string, input: { revision: number; from: number; to: number; instruction: string }) =>
  data(request<{ data: EditProposal }>(`${BASE}/drafts/${id}/edit`, { body: input }))
export const reviewDraft = (id: string, revision: number) => data(request<{ data: Review }>(`${BASE}/drafts/${id}/review`, { body: { revision } }))
export const archiveDraft = (id: string, revision: number, requestId: string) => data(request<{ data: Entry }>(`${BASE}/drafts/${id}/archive`, { body: { revision, requestId } }))
export const getEntry = (id: string) => data(request<{ data: Entry }>(`${BASE}/entries/${id}`))
export const reopenEntry = (id: string) => data(request<{ data: Draft }>(`${BASE}/entries/${id}/reopen`, { body: {} }))
export const learning = () => data(request<{ data: Profile }>(`${BASE}/learning`))
export const getWeekly = (week: string) => data(request<{ data: Weekly | null }>(`${BASE}/weekly/${week}`))
export const generateWeekly = (week: string) => data(request<{ data: Weekly }>(`${BASE}/weekly/${week}/generate`, { body: {} }))
export const publishWeekly = (week: string, title: string, body: string) => data(request<{ data: Weekly }>(`${BASE}/weekly/${week}/publish`, { body: { title, body } }))

export type CoachEvent = { type: 'delta'; text: string } | { type: 'done'; conversation: ChatMessage[] } | { type: 'error'; error: string }

/** 和教练说话：流式返回。第一个字之前的错误（版本对不上、超额……）会直接抛出。 */
export async function* coach(id: string, input: { revision: number; message?: string; mode?: 'ask' | 'hint'; selection?: { from: number; to: number } }, signal?: AbortSignal): AsyncGenerator<CoachEvent> {
  const res = await request<Response>(`${BASE}/drafts/${id}/coach`, { body: input, raw: true, signal, headers: { Accept: 'text/event-stream' } })
  if (!res.body) throw new Error('写作教练没有返回内容')
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  const parser = createSseParser()
  try {
    for (;;) {
      const { value, done } = await reader.read()
      for (const ev of done ? parser.flush() : parser.push(decoder.decode(value, { stream: true }))) {
        if (ev.type === 'delta' && typeof ev.text === 'string') yield { type: 'delta', text: ev.text }
        else if (ev.type === 'done' && Array.isArray(ev.conversation)) yield { type: 'done', conversation: ev.conversation as ChatMessage[] }
        else if (ev.type === 'error') yield { type: 'error', error: typeof ev.error === 'string' ? ev.error : '写作教练出错了' }
      }
      if (done) break
    }
  } finally {
    reader.releaseLock()
  }
}

// ———— 日期 ————

const TZ = 'Asia/Shanghai'

/** 北京时间的 YYYY-MM-DD。 */
export const localDate = (d: Date | string) => new Intl.DateTimeFormat('en-CA', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d))

/** 某天所在的 ISO 周，比如 2026-W41。 */
export function isoWeek(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  const day = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - day + 3) // 这一周的周四决定属于哪一年
  const year = d.getUTCFullYear()
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const week = 1 + Math.round(((d.getTime() - jan4.getTime()) / 86_400_000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7)
  return `${year}-W${String(week).padStart(2, '0')}`
}

/** 上一周（以今天的北京时间算）。 */
export const lastWeek = (now = new Date()) => isoWeek(localDate(new Date(now.getTime() - 7 * 86_400_000)))

/** 「10月11日 · 星期六」 */
export const dayLabel = (d: Date | string) => new Intl.DateTimeFormat('zh-CN', { timeZone: TZ, month: 'long', day: 'numeric', weekday: 'long' }).format(new Date(d)).replace(/日(星期)/, '日 · $1')

export const countWords = (text: string) => (text.match(/[\p{L}\p{N}]+(?:['’.-][\p{L}\p{N}]+)*/gu) ?? []).length
